import React from 'react';
import { Card, CardContent, CardProps, SxProps, Theme, useTheme, alpha, Chip } from '@mui/material';
import { Typography, Box } from '@mui/material';
import { TrendingUp, TrendingDown, Remove } from '@mui/icons-material';
import { neuColors } from '../../../styles/neumorphism';

// ─── Shared helpers ───────────────────────────────────────────────────────────

const getColorValue = (theme: Theme, colorName: string) => {
  // @ts-ignore - Dynamic access to palette colors
  return theme.palette[colorName]?.main || theme.palette.primary.main;
};

const getLightColorValue = (theme: Theme, colorName: string) => {
  // @ts-ignore
  return alpha(theme.palette[colorName]?.main || theme.palette.primary.main, 0.1);
};

const getTrendColor = (trend?: 'up' | 'down' | 'neutral') => {
  if (trend === 'up') return 'success.main';
  if (trend === 'down') return 'error.main';
  return 'text.secondary';
};

const getTrendIcon = (trend?: 'up' | 'down' | 'neutral') => {
  if (trend === 'up') return <TrendingUp fontSize="small" />;
  if (trend === 'down') return <TrendingDown fontSize="small" />;
  return <Remove fontSize="small" />;
};

const formatValue = (val: string | number | null | undefined): string => {
  if (val == null) return '—';
  const num = typeof val === 'string' ? parseFloat(val.replace(/,/g, '')) : val;
  if (isNaN(num)) return String(val);
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1)}M`;
  if (num >= 1_000) return `${(num / 1_000).toFixed(0)}K`;
  return String(val);
};

// ─── Shared types ─────────────────────────────────────────────────────────────

export interface StatsCardProps {
  title: string;
  value: string | number | null | undefined;
  subtitle?: string;
  trend?: 'up' | 'down' | 'neutral';
  trendLabel?: string;
  color?: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info';
  icon?: React.ReactNode;
  sx?: SxProps<Theme>;
  date?: string;
  description?: string;
  status?: string;
  candidateCount?: number;
}

// ─── StandardCard ─────────────────────────────────────────────────────────────

export interface StandardCardProps extends Omit<CardProps, 'children'> {
  title?: string;
  subtitle?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
  sx?: SxProps<Theme>;
}

export const StandardCard: React.FC<StandardCardProps> = ({
  title,
  subtitle,
  action,
  children,
  sx = {},
  ...props
}) => (
  <Card
    sx={[
      {
        borderRadius: 2,
        border: 'none',
        bgcolor: 'background.paper',
        boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
          transform: 'translateY(-2px)',
        },
      },
      ...(Array.isArray(sx) ? sx : [sx]),
    ]}
    {...props}
  >
    {(title || subtitle || action) && (
      <Box sx={{ p: 3, pb: subtitle || children ? 2 : 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
          <Box sx={{ flex: 1 }}>
            {title && (
              <Typography variant="h6" component="h3" sx={{ fontWeight: 600, mb: subtitle ? 0.5 : 0, color: neuColors.text }}>
                {title}
              </Typography>
            )}
            {subtitle && (
              <Typography variant="body2" sx={{ color: neuColors.textLight }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          {action && (
            <Box sx={{ flexShrink: 0, ml: 2 }}>
              {action}
            </Box>
          )}
        </Box>
      </Box>
    )}
    <CardContent sx={{ pt: title || subtitle ? 1 : 3, ...(title || subtitle || action ? {} : { p: 3 }) }}>
      {children}
    </CardContent>
  </Card>
);

// ─── CompactCard ──────────────────────────────────────────────────────────────

export const CompactCard: React.FC<StandardCardProps> = ({
  title,
  children,
  sx = {},
  ...props
}) => (
  <Card
    sx={[
      {
        borderRadius: 2,
        border: 'none',
        bgcolor: 'background.paper',
        boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
        },
      },
      ...(Array.isArray(sx) ? sx : [sx]),
    ]}
    {...props}
  >
    {title && (
      <Box sx={{ px: 2, pt: 2, pb: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: neuColors.text }}>
          {title}
        </Typography>
      </Box>
    )}
    <Box sx={{ px: 2, pb: 2, ...(title ? {} : { p: 2 }) }}>
      {children}
    </Box>
  </Card>
);

// ─── CompactStatsCard (also exported as StatsCard) ────────────────────────────

export const CompactStatsCard: React.FC<StatsCardProps> = ({
  title, value, subtitle, trend, trendLabel, color = 'primary', icon, sx = {},
}) => {
  const theme = useTheme();
  const mainColor = getColorValue(theme, color);
  const lightColor = getLightColorValue(theme, color);

  return (
    <Card
      elevation={0}
      sx={[
        {
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          height: '100%',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            boxShadow: '0 4px 16px rgba(15,23,42,0.1)',
            transform: 'translateY(-1px)',
          },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 600,
              color: 'text.secondary',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              fontSize: '0.7rem',
            }}
          >
            {title}
          </Typography>
          {icon && (
            <Box sx={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: 28, height: 28, borderRadius: 1.5,
              bgcolor: lightColor, color: mainColor, flexShrink: 0,
              '& .MuiSvgIcon-root': { fontSize: '1rem' },
            }}>
              {icon}
            </Box>
          )}
        </Box>
        <Typography
          variant="h5"
          fontWeight={700}
          sx={{ color: 'text.primary', lineHeight: 1, mb: subtitle || (trend && trendLabel) ? 0.5 : 0 }}
        >
          {formatValue(value)}
        </Typography>
        {(subtitle || (trend && trendLabel)) && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            {trend && trendLabel && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, color: getTrendColor(trend) }}>
                {getTrendIcon(trend)}
                <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>{trendLabel}</Typography>
              </Box>
            )}
            {subtitle && <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>{subtitle}</Typography>}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

// StatsCard is an alias for CompactStatsCard
export const StatsCard = CompactStatsCard;

// ─── MetricCard (Reportiy-style: icon + uppercase label → bold value → subtitle) ─

export interface MetricCardProps {
  label: string;
  value: string | number | null | undefined;
  subtitle?: string;
  icon?: React.ReactNode;
  color?: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info';
  trend?: 'up' | 'down' | 'neutral';
  trendLabel?: string;
  sx?: SxProps<Theme>;
}

export const MetricCard: React.FC<MetricCardProps> = ({
  label, value, subtitle, icon, color = 'primary', trend, trendLabel, sx = {},
}) => {
  const theme = useTheme();
  const mainColor = getColorValue(theme, color);

  return (
    <Card
      elevation={0}
      sx={[
        {
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          height: '100%',
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          {icon && (
            <Box sx={{
              color: mainColor,
              display: 'flex',
              '& .MuiSvgIcon-root': { fontSize: '1.1rem' },
            }}>
              {icon}
            </Box>
          )}
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              color: 'text.secondary',
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              fontSize: '0.7rem',
            }}
          >
            {label}
          </Typography>
        </Box>
        <Typography
          variant="h4"
          fontWeight={700}
          sx={{ color: 'text.primary', lineHeight: 1, mb: subtitle || trendLabel ? 0.75 : 0 }}
        >
          {formatValue(value)}
        </Typography>
        {(subtitle || trendLabel) && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            {trend && trendLabel && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, color: getTrendColor(trend) }}>
                {getTrendIcon(trend)}
                <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>{trendLabel}</Typography>
              </Box>
            )}
            {subtitle && (
              <Typography variant="body2" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

// ─── InfoCards (full-size card with date, status, description) ────────────────

export const InfoCards: React.FC<StatsCardProps> = ({
  title, value, subtitle, trend, trendLabel, color = 'primary', icon,
  date, description, status, candidateCount,
}) => {
  const theme = useTheme();
  const mainColor = getColorValue(theme, color);
  const lightColor = getLightColorValue(theme, color);

  return (
    <Card sx={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      transition: 'all 0.3s ease',
      '&:hover': {
        transform: 'translateY(-4px)',
      }
    }}>
      <CardContent sx={{
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
        p: 3,
      }}>
        {/* Header */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
            {title}
          </Typography>
          {date && (
            <Typography variant="body2" color="text.secondary">
              {date}
            </Typography>
          )}
        </Box>

        {/* Status and Candidates */}
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          {status && (
            <Chip label={status} size="small" color="primary" />
          )}
          {candidateCount && (
            <Chip label={`${candidateCount} Candidates`} size="small" />
          )}
        </Box>

        {/* Description */}
        {description && (
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {description}
            </Typography>
          </Box>
        )}

        {/* Value Display */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {icon && (
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 32,
                height: 32,
                borderRadius: '50%',
                bgcolor: lightColor,
                color: mainColor,
                '& .MuiSvgIcon-root': { fontSize: '1.2rem' }
              }}>
                {icon}
              </Box>
            )}
            <Typography variant="h4" fontWeight={700}>
              {formatValue(value)}
            </Typography>
          </Box>

          {/* Trend indicator */}
          {(trend || trendLabel) && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: getTrendColor(trend) }}>
              {getTrendIcon(trend)}
              <Typography variant="caption" sx={{ fontWeight: 700 }}>
                {trendLabel}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Subtitle */}
        {subtitle && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

export default {
  StandardCard,
  CompactCard,
  InfoCards,
  StatsCard,
  CompactStatsCard,
  MetricCard,
};
